import React, { useState, useCallback, useEffect, useContext } from 'react';
import { Box } from '@mui/material';
import { AccessDataContext } from 'contextProviders/AccessDataContext';
import OverlayProvider from 'contextProviders/OverlayContext';
import { TabPanel } from 'devextreme-react';
import { SnackbarProvider } from 'notistack';

import { IClientPricelistData } from 'interfaces/Pricelist/IClientPricelistData';
import { UserRoles } from 'enums/UserRoles';
import { PricelistTabs } from 'enums/PricelistTabs';

import AnalysisInfo from '../../MasterDetailPanel/components/AnalysisInfo/AnalysisInfo';
import AnalyteInfo from '../../MasterDetailPanel/components/AnalyteInfo/AnalyteInfo';
import GroupAnalysis from '../../MasterDetailPanel/components/GroupAnalysis/GroupAnalysis';
import MediaInfo from '../../MasterDetailPanel/components/MediaInfo/MediaInfo';
import OtherInfo from '../../MasterDetailPanel/components/OtherInfo/OtherInfo';
import PrepAnalysis from '../../MasterDetailPanel/components/PrepAnalysis/PrepAnalysis';
import SubAnalysis from '../../MasterDetailPanel/components/SubAnalysis/SubAnalysis';

const ClientPricelistDetailView = (detailData: IClientPricelistData) => {
  const { securityRoles } = useContext(AccessDataContext);
  const [selectedTab, setSelectedTab] = useState(0);
  const [availableTabs, setAvailableTabs] = useState([]);

  const renderTab = useCallback(
    (tab) => {
      switch (tab) {
        case PricelistTabs.AnalysisInfo:
          return <AnalysisInfo analysis={detailData.Analysis} matrix={detailData.DefaultMatrix} />;
        case PricelistTabs.AnalyteInfo:
          return <AnalyteInfo analysis={detailData.Analysis} matrix={detailData.DefaultMatrix} />;
        case PricelistTabs.GroupAnalysis:
          return <GroupAnalysis analysis={detailData.Analysis} matrix={detailData.DefaultMatrix} />;
        case PricelistTabs.PrepAnalysis:
          return <PrepAnalysis analysis={detailData.Analysis} matrix={detailData.DefaultMatrix} />;
        case PricelistTabs.SubAnalysis:
          return <SubAnalysis analysis={detailData.Analysis} matrix={detailData.DefaultMatrix} />;
        case PricelistTabs.MediaInfo:
          return <MediaInfo analysis={detailData.Analysis} matrix={detailData.DefaultMatrix} />;
        case PricelistTabs.OtherInfo:
          return <OtherInfo analysis={detailData.Analysis} matrix={detailData.DefaultMatrix} />;
        default:
          return null;
      }
    },
    [detailData]
  );

  const tabSelectionChanged = useCallback(
    (e) => {
      setSelectedTab(availableTabs.indexOf(e.addedItems[0].title));
    },
    [availableTabs]
  );

  useEffect(() => {
    const tabs = [];

    if(!detailData.Analysis || !detailData.Client){
      return;
    }

    if (detailData.IsTracking) {
      tabs.push(PricelistTabs.AnalysisInfo);

      if (detailData.IsPrepFee) {
        tabs.push(PricelistTabs.PrepAnalysis);
      }
    } else if (detailData.IsStandardGroup) {
      tabs.push(PricelistTabs.SubAnalysis);
      tabs.push(PricelistTabs.GroupAnalysis);
    } else {
      tabs.push(PricelistTabs.AnalyteInfo);
      tabs.push(PricelistTabs.AnalysisInfo);
    }

    if (detailData.HasMedia) {
      tabs.push(PricelistTabs.MediaInfo);
    }

    if (securityRoles?.some((f) => f === UserRoles.Admin)) {
      tabs.push(PricelistTabs.OtherInfo);
    }

    setAvailableTabs(tabs);
  }, [detailData, securityRoles]);

  return (
    <Box className="clientDetailView">
      <TabPanel
        swipeEnabled={false}
        selectedIndex={selectedTab}
        onSelectionChanged={tabSelectionChanged}
        itemComponent={({ data }) => <OverlayProvider><SnackbarProvider maxSnack={3}>{renderTab(data.title)}</SnackbarProvider></OverlayProvider>}
        dataSource={availableTabs.map((m) => ({ title: m }))}
      />
    </Box>
  );
};

export default ClientPricelistDetailView;
